/**
 * DetailCoverageCard — Shows PO detail extraction coverage stats
 * "4,530 / 13,872 POs have detail data (32.7%)"
 * Collapsible: per-dept breakdown + "Queue Missing" action
 */
import React, { useState, useEffect, useCallback } from 'react';
import { confirmDialog } from '../ConfirmDialog';
import { motion, AnimatePresence } from 'framer-motion';
import { FileSearch, ChevronDown, ChevronRight, RefreshCw, Zap, AlertTriangle, CheckCircle } from 'lucide-react';
import apiClient from '../../lib/apiClient';
import './DetailCoverageCard.css';

function fmt(n) { return parseInt(n || 0).toLocaleString(); }
function fmtSpend(n) {
  const v = parseFloat(n || 0);
  if (v >= 1e9) return `$${(v/1e9).toFixed(1)}B`;
  if (v >= 1e6) return `$${(v/1e6).toFixed(1)}M`;
  if (v >= 1e3) return `$${(v/1e3).toFixed(0)}K`;
  return `$${v.toFixed(0)}`;
}

function CoverageBar({ pct, size = 'md' }) {
  const color = pct >= 80 ? '#10b981' : pct >= 40 ? '#f59e0b' : '#ef4444';
  return (
    <div className={`dccard-bar dccard-bar--${size}`}>
      <div className="dccard-bar-track">
        <motion.div
          className="dccard-bar-fill"
          style={{ background: color }}
          initial={{ width: 0 }}
          animate={{ width: `${Math.min(pct, 100)}%` }}
          transition={{ type: 'spring', stiffness: 100, damping: 20, delay: 0.05 }}
        />
      </div>
      <span className="dccard-bar-pct" style={{ color }}>{pct.toFixed(1)}%</span>
    </div>
  );
}

export default function DetailCoverageCard({ className }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [deptExpanded, setDeptExpanded] = useState(false);
  const [queueing, setQueueing] = useState(null); // dept_code being queued

  const load = useCallback(async () => {
    try {
      const res = await apiClient.get('/api/detail-coverage/summary');
      setData(res);
    } catch (e) {
      console.warn('DetailCoverageCard fetch error:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleQueueDept = async (deptCode, totalPos, withDetail) => {
    const missing = totalPos - withDetail;
    if (missing === 0) return;
    if (!(await confirmDialog(`Queue ${missing.toLocaleString()} POs from ${deptCode} for detail extraction?`))) return;

    setQueueing(deptCode);
    try {
      // Fetch missing POs for this dept then queue them
      const missing_pos = await apiClient.get(`/api/detail-coverage/missing?dept=${deptCode}&limit=100&sort=spend`);
      const doc_nums = missing_pos.map(p => p.purchase_document_num).filter(Boolean);
      if (doc_nums.length) {
        await apiClient.post('/api/detail-coverage/queue', {
          document_numbers: doc_nums,
          department_code: deptCode,
        });
        await load();
      }
    } catch (e) {
      console.error('Queue failed:', e);
    } finally {
      setQueueing(null);
    }
  };

  if (loading) return (
    <div className={`dccard ${className || ''}`}>
      <div className="dccard-loading"><RefreshCw size={16} className="dccard-spin" /> Loading detail coverage...</div>
    </div>
  );

  if (!data) return null;

  const pct = data.coverage_pct || 0;
  const pctColor = pct >= 80 ? '#10b981' : pct >= 40 ? '#f59e0b' : '#ef4444';

  return (
    <div className={`dccard ${className || ''}`}>
      {/* Header row */}
      <div className="dccard-header" onClick={() => setExpanded(e => !e)}>
        <div className="dccard-header-left">
          <FileSearch size={15} className="dccard-icon" />
          <span className="dccard-title">Detail Coverage</span>
          <span className="dccard-badge" style={{ background: `${pctColor}22`, color: pctColor, borderColor: `${pctColor}44` }}>
            {fmt(data.with_detail)} / {fmt(data.total_pos)} POs
          </span>
        </div>
        <div className="dccard-header-right">
          <button className="dccard-refresh" onClick={e => { e.stopPropagation(); load(); }}>
            <RefreshCw size={12} />
          </button>
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </div>
      </div>

      <AnimatePresence>
        {expanded && (
          <motion.div
            className="dccard-body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          >
            {/* Big stats */}
            <div className="dccard-stats">
              <div className="dccard-stat">
                <span className="dccard-stat-val" style={{ color: pctColor }}>{pct.toFixed(1)}%</span>
                <span className="dccard-stat-label">detail coverage</span>
              </div>
              <div className="dccard-stat dccard-stat--green">
                <span className="dccard-stat-val">{fmt(data.with_detail)}</span>
                <span className="dccard-stat-label">POs with detail</span>
              </div>
              <div className="dccard-stat dccard-stat--red">
                <span className="dccard-stat-val">{fmt(data.missing_detail)}</span>
                <span className="dccard-stat-label">POs missing</span>
              </div>
              <div className="dccard-stat">
                <span className="dccard-stat-val">{fmtSpend(data.missing_spend)}</span>
                <span className="dccard-stat-label">spend uncovered</span>
              </div>
            </div>

            {/* Overall bar */}
            <div className="dccard-overall">
              <span className="dccard-overall-label">Overall</span>
              <CoverageBar pct={pct} size="lg" />
            </div>

            {/* Queue status */}
            {(data.queue.pending > 0 || data.queue.failed > 0) && (
              <div className="dccard-queue-status">
                {data.queue.pending > 0 && (
                  <span className="dccard-queue-item dccard-queue-pending">
                    <Zap size={11} /> {data.queue.pending} queued
                  </span>
                )}
                {data.queue.failed > 0 && (
                  <span className="dccard-queue-item dccard-queue-failed">
                    <AlertTriangle size={11} /> {data.queue.failed} failed
                  </span>
                )}
                {data.queue.complete > 0 && (
                  <span className="dccard-queue-item dccard-queue-complete">
                    <CheckCircle size={11} /> {data.queue.complete} done
                  </span>
                )}
              </div>
            )}

            {/* Per-dept breakdown */}
            {data.by_dept?.length > 0 && (
              <div className="dccard-dept-section">
                <button className="dccard-dept-toggle" onClick={() => setDeptExpanded(e => !e)}>
                  {deptExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                  {data.by_dept.length} departments
                </button>
                <AnimatePresence>
                  {deptExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                      className="dccard-dept-list"
                    >
                      {data.by_dept.map(d => (
                        <div key={d.dept_code} className="dccard-dept-row">
                          <span className="dccard-dept-code">{d.dept_code}</span>
                          <span className="dccard-dept-pos">{fmt(d.with_detail)}/{fmt(d.total_pos)}</span>
                          <div className="dccard-dept-bar"><CoverageBar pct={d.detail_pct} size="sm" /></div>
                          {d.with_detail < d.total_pos && (
                            <button
                              className="dccard-queue-btn"
                              disabled={queueing === d.dept_code}
                              onClick={() => handleQueueDept(d.dept_code, d.total_pos, d.with_detail)}
                              title={`Queue ${(d.total_pos - d.with_detail).toLocaleString()} missing POs`}
                            >
                              {queueing === d.dept_code
                                ? <RefreshCw size={10} className="dccard-spin" />
                                : <><Zap size={10} /> Queue</>}
                            </button>
                          )}
                        </div>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
